import { Grid, Paper, Button, Modal, Accordion, AccordionSummary, AccordionDetails, Select, MenuItem, TextField } from "@mui/material"; // Import MUI components
import React, { useState } from "react";
import "../../Styles/strategy.css";
import HeaderIcons from "../../Images/headericon.png";
import InvestorManagement from "../Tables/InvestorManagement";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Import the expand icon
import {modifyHurdleRate} from '../../Redux/Actions/strategyService'
import { useDispatch } from "react-redux";

function Index() {
  const [isButtonEnabled, setIsButtonEnabled] = useState(false); // State to manage button enable/disable
  const [selectedUsersData, setSelectedUsersData] = useState([]); // Store the selected user data
  const [openModal, setOpenModal] = useState(false); // Control modal visibility
  const [hurdleRateError, setHurdleRateError] = useState(""); // State for hurdle rate error
  const [perfFeeError, setPerfFeeError] = useState(""); // State for performance fee error
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for success modal
  const [showErrorModal, setShowErrorModal] = useState(false); // State for error modal
  const [errorMessage, setErrorMessage] = useState(""); // State for error message
  const dispatch= useDispatch()

  // Define benchmark options using the provided data
  const benchmarkOptions = {
    "^GSPC": "S&P 500",
    "^IXIC": "NASDAQ",
    "^DJI": "Dow Jones",
    "^RUT": "Russell 2000",
    "EEM": "MSCI Emerging",
    "^FTSE": "FTSE 100",
    "XLK": "Technology Select",
    "BND": "Vanguard Total Bond",
    "GC=F": "Gold Spot Price",
    "CL=F": "Crude Oil (WTI)"
  };

  const [formValues, setFormValues] = useState({
    hurdleRateApplicable: "",
    hurdleRateType: "",
    hurdleRate: "",
    selectedBenchmark: "", // This will store the symbol directly
    performanceFees: "",
    managementFeesTiers: [{ limit: 0, fee: "" }], // Initialize with one tier
  });
  const [errors, setErrors] = useState({}); // State to store validation errors

  const handleSelectionChange = (selectedRows) => {
    setIsButtonEnabled(selectedRows.length > 0);
    setSelectedUsersData(selectedRows); // Store selected rows data
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setErrors({}); // Clear errors on modal close
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  // Hurdle Rate Change Handler
  const handleHurdleRateValueChange = (value) => {
    const numericValue = parseFloat(value);
    if (value === "") {
      setHurdleRateError("Hurdle Rate is required."); // Set error if empty
      setFormValues((prev) => ({ ...prev, hurdleRate: "" })); // Clear value
    } else if (numericValue < 0 || numericValue > 100) {
      setHurdleRateError("Hurdle Rate must be between 0 and 100."); // Set error if out of range
      setFormValues((prev) => ({ ...prev, hurdleRate: "" })); // Clear value
    } else {
      setHurdleRateError(""); // Clear error if valid
      setFormValues((prev) => ({ ...prev, hurdleRate: value })); // Update value
    }
  };

  // Performance Fee Change Handler
  const handlePerfFeeChange = (value) => {
    const numericValue = parseFloat(value);
    if (value === "") {
      setPerfFeeError("Performance Fee is required."); // Set error if empty
      setFormValues((prev) => ({ ...prev, performanceFees: "" })); // Clear value
    } else if (numericValue < 0 || numericValue > 100) {
      setPerfFeeError("Performance Fee must be between 0 and 100."); // Set error if out of range
      setFormValues((prev) => ({ ...prev, performanceFees: "" })); // Clear value
    } else {
      setPerfFeeError(""); // Clear error if valid
      setFormValues((prev) => ({ ...prev, performanceFees: value })); // Update value
    }
  };

  // Add a new tier to management fees
  const addManagementFeeTier = () => {
    setFormValues(prev => ({
      ...prev,
      managementFeesTiers: [...prev.managementFeesTiers, { limit: 0, fee: "" }]
    }));
  };

  // Remove a tier from management fees
  const removeManagementFeeTier = (index) => {
    if (formValues.managementFeesTiers.length > 1) {
      setFormValues(prev => ({
        ...prev,
        managementFeesTiers: prev.managementFeesTiers.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle changes to management fee tiers
  const handleTierChange = (index, field, value) => {
    // For limit field, enforce minimum value of 1
    if (field === "limit" && value !== "") {
      const numericValue = parseInt(value);
      if (numericValue < 1) {
        return; // Don't update if value is less than 1
      }
    }
    
    const updatedTiers = [...formValues.managementFeesTiers];
    updatedTiers[index][field] = value;
    setFormValues(prev => ({
      ...prev,
      managementFeesTiers: updatedTiers
    }));
  };

  // Validate tier values
  const validateTier = (tier) => {
    const fee = parseFloat(tier.fee);
    return !(isNaN(fee) || fee < 0 || fee > 100);
  };

  // Update the validate function
  const validate = () => {
    const newErrors = {};

    // Validate management fee tiers
    const tierErrors = formValues.managementFeesTiers.map(tier => !validateTier(tier));
    if (tierErrors.some(error => error)) {
      newErrors.managementFeesTiers = "All management fee tiers must have valid fees between 0 and 100";
    }

    // Performance fee validation
    if (formValues.performanceFees === "") {
      newErrors.performanceFees = "Performance Fee is required";
    } else if (parseFloat(formValues.performanceFees) < 0 || parseFloat(formValues.performanceFees) > 100) {
      newErrors.performanceFees = "Performance Fee must be between 0 and 100";
    }

    if (formValues.hurdleRateApplicable === "Yes") {
      if (!formValues.hurdleRateType) {
        newErrors.hurdleRateType = "Required";
      }
      if (formValues.hurdleRateType === "FIXED") {
        if (!formValues.hurdleRate) {
          newErrors.hurdleRate = "Required";
        } else if (formValues.hurdleRate > 100) {
          newErrors.hurdleRate = "Must be 100 or less";
        }
      }
      if (formValues.hurdleRateType === "BENCHMARK") {
        if (!formValues.selectedBenchmark) {
          newErrors.selectedBenchmark = "Required";
        }
      }
    }
    return newErrors;
  };

  // Handle success response
  const handleSuccess = () => {
    setShowSuccessModal(true);
    handleCloseModal();
  };

  // Handle error response
  const handleError = (message) => {
    setErrorMessage(message || "An error occurred while updating details.");
    setShowErrorModal(true);
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return; // Stop submission if there are errors
    }

    // Create the array of strategy objects with the new structure
    const strategies = selectedUsersData.map(user => ({
      id: user.id, // Use the selected record's ID
      managementFeesTiers: formValues.managementFeesTiers.map(tier => ({
        limit: parseInt(tier.limit) || 1,
        fee: parseFloat(tier.fee)
      })),
      performanceFeesPercentage: parseFloat(formValues.performanceFees) || 0,
      hurdleRateType: formValues.hurdleRateApplicable === "Yes" ? formValues.hurdleRateType : "FIXED",
      hurdleRatePercentage: formValues.hurdleRateApplicable === "Yes" && formValues.hurdleRateType === "FIXED" 
        ? parseFloat(formValues.hurdleRate) || 0 
        : 0,
      performanceBenchmark: formValues.hurdleRateApplicable === "Yes" && formValues.hurdleRateType === "BENCHMARK" 
        ? {
            symbol: formValues.selectedBenchmark,
            name: benchmarkOptions[formValues.selectedBenchmark] || ""
          } 
        : {
            name: "",
            symbol: ""
          }
    }));
    // Send strategies array directly
    dispatch(modifyHurdleRate(strategies))
      .then(response => {
        if (response && response.payload.statusCode === 200) {
          handleSuccess();
        } else {
          handleError(response?.data?.message);
        }
      })
      .catch(error => {
        handleError(error?.response?.data?.message);
      });
  };

  // Close success modal
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  // Close error modal
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
  };

  return (
    <>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }} elevation={1}>
          <div className="row rows" style={{ marginLeft: "2px" }}>
            <div className="col-1">
              <img src={HeaderIcons} alt="Header Icon" width="45" height="45" />
            </div>
            <div className="col-9" style={{ marginTop: "10px", marginLeft: "-3%" }}>
              <p style={{ fontSize: "20px", color: "#18275A" }}> Investor Management</p>
            </div>
            <div className="col-2" style={{ textAlign: "right" }}>
              <Button variant="contained" color="primary" disabled={!isButtonEnabled} onClick={handleOpenModal}>
                Update details
              </Button>
            </div>
          </div>
        </Paper>
      </Grid>
      <div style={{ height: "calc(100vh - 170px)", overflowY: "auto", overflowX: "hidden" }}>
        <Grid item xs={12}>
          <div className="row">
            <div className="col-12">
              <InvestorManagement onSelectionChange={handleSelectionChange} />
            </div>
          </div>
        </Grid>
      </div>

      {/* Modal to display selected users */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <div style={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: "white",
          maxWidth: "800px",
          width: '90%',
          maxHeight: '90vh', // Set maximum height to 90% of viewport height
          overflowY: 'auto', // Enable vertical scrolling
          padding: "20px",
          borderRadius: '8px'
        }}>
          <h2>Selected Users Details</h2>
          <Accordion style={{ border: "1px solid", borderRadius: "10px" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <p>View Selected Users</p>
            </AccordionSummary>
            <AccordionDetails style={{ maxHeight: "300px", overflowY: "auto" }}>
              {selectedUsersData.length > 0 ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #ccc", padding: "8px" }}>Strategy Name</th>
                      <th style={{ border: "1px solid #ccc", padding: "8px" }}>Custodains</th>
                      <th style={{ border: "1px solid #ccc", padding: "8px" }}>Banchmark Type</th>
                      <th style={{ border: "1px solid #ccc", padding: "8px" }}>Hurdle % Type</th>
                      <th style={{ border: "1px solid #ccc", padding: "8px" }}>Hurdle Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUsersData.map((user, index) => (
                      <tr key={index}>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>{user.strategy.name}</td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>{user.strategy.custodian}</td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>{user.benchmarkType}</td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>{user.hurdleRatePercentage}</td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>{user.hurdleRateType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No users selected</p>
              )}
            </AccordionDetails>
          </Accordion>
          
          {/* Management Fees Tiers Section */}
          <div style={{ marginTop: "20px" }}>
            <h3>Management Fees Tiers</h3>
            {formValues.managementFeesTiers.map((tier, index) => (
              <div key={index} style={{ display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center" }}>
                <TextField
                  type="number"
                  label="Limit"
                  value={tier.limit}
                  onChange={(e) => handleTierChange(index, "limit", e.target.value)}
                  inputProps={{ min: 1 }}
                  style={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  label="Fee (%)"
                  value={tier.fee}
                  onChange={(e) => handleTierChange(index, "fee", e.target.value)}
                  inputProps={{ min: 0, max: 100 }}
                  style={{ flex: 1 }}
                />
                {formValues.managementFeesTiers.length > 1 && (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={() => removeManagementFeeTier(index)}
                    style={{ minWidth: "40px", padding: "5px" }}
                  >
                    X
                  </Button>
                )}
              </div>
            ))}
            <Button 
              variant="outlined" 
              onClick={addManagementFeeTier}
              style={{ marginTop: "10px" }}
            >
              Add Tier
            </Button>
            {errors.managementFeesTiers && (
              <p style={{ color: 'red', fontSize: '11px' }}>
                {errors.managementFeesTiers}
              </p>
            )}
          </div>
          
          {/* Performance Fees Field */}
          <TextField
            type="number"
            name="performanceFees"
            value={formValues.performanceFees}
            onChange={(e) => handlePerfFeeChange(e.target.value)}
            label="Performance Fees (%)"
            inputProps={{ max: 100 }}
            fullWidth
            style={{ marginTop: "20px" }}
          />
          {perfFeeError && (
            <p style={{ color: 'red', fontSize: '11px' }}>
              {perfFeeError}
            </p>
          )}
          
          {/* Fields below the accordion in the modal */}
          <div style={{ marginTop: "20px" }}>
            <Select name="hurdleRateApplicable" value={formValues.hurdleRateApplicable} onChange={handleChange} displayEmpty fullWidth>
              <MenuItem value="" disabled>
                Hurdle rate applicable
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>

            {formValues.hurdleRateApplicable === "Yes" && (
              <>
                <Select name="hurdleRateType" value={formValues.hurdleRateType} onChange={handleChange} displayEmpty fullWidth style={{ marginTop: "10px" }}>
                  <MenuItem value="" disabled>
                    Hurdle Rate Type
                  </MenuItem>
                  <MenuItem value="FIXED">Fixed</MenuItem>
                  <MenuItem value="BENCHMARK">Benchmark</MenuItem>
                </Select>
                {errors.hurdleRateType && <div style={{ color: 'red' }}>{errors.hurdleRateType}</div>}

                {formValues.hurdleRateType === "FIXED" && (
                  <TextField
                    type="number"
                    name="hurdleRate"
                    value={formValues.hurdleRate}
                    onChange={(e) => handleHurdleRateValueChange(e.target.value)}
                    label="Hurdle Rate (%)"
                    inputProps={{ max: 100 }}
                    fullWidth
                    style={{ marginTop: "10px" }}
                  />
                )}
                {hurdleRateError && (
                  <p style={{ color: 'red', fontSize: '11px' }}>
                    {hurdleRateError}
                  </p>
                )}
                
                {formValues.hurdleRateType === "BENCHMARK" && (
                  <>
                    <Select 
                      name="selectedBenchmark" 
                      value={formValues.selectedBenchmark} 
                      onChange={handleChange} 
                      displayEmpty 
                      fullWidth 
                      style={{ marginTop: "10px" }}
                    >
                      <MenuItem value="" disabled>
                        Select Benchmark
                      </MenuItem>
                      {Object.entries(benchmarkOptions).map(([symbol, name]) => (
                        <MenuItem key={symbol} value={symbol}>
                          {name} ({symbol})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.selectedBenchmark && <div style={{ color: 'red' }}>{errors.selectedBenchmark}</div>}
                  </>
                )}
              </>
            )}
          </div>
          <br />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button type="button" onClick={handleCloseModal} variant="contained" color="primary">
              Close
            </Button>
            <Button type="button" onClick={handleSubmit} variant="contained" color="secondary">
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal open={showSuccessModal} onClose={handleCloseSuccessModal}>
        <div style={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: "white",
          width: '400px',
          padding: "20px",
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'green' }}>Success!</h2>
          <p>Details have been updated successfully.</p>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleCloseSuccessModal}
            style={{ marginTop: '20px' }}
          >
            Close
          </Button>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal open={showErrorModal} onClose={handleCloseErrorModal}>
        <div style={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: "white",
          width: '400px',
          padding: "20px",
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'red' }}>Error</h2>
          <p>{errorMessage}</p>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleCloseErrorModal}
            style={{ marginTop: '20px' }}
          >
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default Index;
